import * as React from "react";
import { useCurrentProject } from "@/lib/project";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Search, MoreHorizontal, Trash2, Edit } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { PageHeader } from "@/components/bespoke";
import { formatDateTime } from "@/lib/format";

export type EvalGrader = {
  id: string;
  projectId: string;
  name: string;
  type: "deterministic" | "rubric" | "human";
  config: Record<string, unknown>;
  version: number;
  promptVersion: string | null;
  createdAt: string;
  updatedAt: string;
};

async function fetchGraders(projectId: string): Promise<EvalGrader[]> {
  const response = await fetch(`/api/v1/projects/${projectId}/eval/graders`, {
    method: "GET",
    credentials: "same-origin",
  });
  if (!response.ok) return [];
  return (await response.json()).graders;
}

async function createGrader(
  projectId: string,
  input: {
    name: string;
    type: "deterministic" | "rubric" | "human";
    config?: Record<string, unknown>;
    version?: number;
    promptVersion?: string | null;
  },
): Promise<EvalGrader> {
  const response = await fetch(`/api/v1/projects/${projectId}/eval/graders`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  return response.json();
}

async function updateGrader(
  projectId: string,
  graderId: string,
  input: {
    name?: string;
    type?: "deterministic" | "rubric" | "human";
    config?: Record<string, unknown>;
    version?: number;
    promptVersion?: string | null;
  },
): Promise<EvalGrader> {
  const response = await fetch(`/api/v1/projects/${projectId}/eval/graders/${graderId}`, {
    method: "PATCH",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  return response.json();
}

async function deleteGrader(projectId: string, graderId: string): Promise<void> {
  await fetch(`/api/v1/projects/${projectId}/eval/graders/${graderId}`, {
    method: "DELETE",
    credentials: "same-origin",
  });
}

function GraderRow({
  grader,
  onEdit,
  onDelete,
}: {
  grader: EvalGrader;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const typeColors: Record<string, "outline" | "secondary" | "info"> = {
    deterministic: "outline",
    rubric: "secondary",
    human: "info",
  };

  return (
    <tr className="group hover:bg-muted/50">
      <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5 font-medium">
        {grader.name}
      </td>
      <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5">
        <Badge variant={typeColors[grader.type] || "outline"}>{grader.type}</Badge>
      </td>
      <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5">
        v{grader.version}
      </td>
      <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5 text-muted-foreground">
        {grader.promptVersion ?? "—"}
      </td>
      <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5 text-muted-foreground">
        {formatDateTime(grader.updatedAt)}
      </td>
      <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5">
        <DropdownMenu>
          <DropdownMenuTrigger>
            <Button variant="ghost" size="icon" className="h-11 w-11 md:h-8 md:w-8">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onEdit}>
              <Edit className="mr-2 h-4 w-4" /> Edit
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onDelete} className="text-destructive-text">
              <Trash2 className="mr-2 h-4 w-4" /> Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </td>
    </tr>
  );
}

export function EvalGradersPage({ headingLevel = "h1" }: { headingLevel?: "h1" | "h2" }) {
  const { project } = useCurrentProject();
  const projectId = project?.id;
  const queryClient = useQueryClient();

  const { data: graders, isLoading } = useQuery({
    queryKey: ["eval-graders", projectId],
    queryFn: () => fetchGraders(projectId!),
    enabled: !!projectId,
  });

  const createMutation = useMutation({
    mutationFn: (input: {
      name: string;
      type: "deterministic" | "rubric" | "human";
      config?: Record<string, unknown>;
      version?: number;
      promptVersion?: string | null;
    }) => createGrader(projectId!, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["eval-graders", projectId] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      graderId,
      input,
    }: {
      graderId: string;
      input: {
        name?: string;
        type?: "deterministic" | "rubric" | "human";
        config?: Record<string, unknown>;
        version?: number;
        promptVersion?: string | null;
      };
    }) => updateGrader(projectId!, graderId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["eval-graders", projectId] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (graderId: string) => deleteGrader(projectId!, graderId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["eval-graders", projectId] });
    },
  });

  const [isCreateOpen, setIsCreateOpen] = React.useState(false);
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [editName, setEditName] = React.useState("");
  const [editType, setEditType] = React.useState<"deterministic" | "rubric" | "human">(
    "deterministic",
  );
  const [editConfig, setEditConfig] = React.useState("");
  const [editVersion, setEditVersion] = React.useState(1);
  const [editPromptVersion, setEditPromptVersion] = React.useState("");
  const [search, setSearch] = React.useState("");

  const filteredGraders =
    graders?.filter(
      (g) =>
        g.name.toLowerCase().includes(search.toLowerCase()) ||
        g.type.toLowerCase().includes(search.toLowerCase()),
    ) ?? [];

  const handleCreate = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    try {
      const config = formData.get("config") ? JSON.parse(formData.get("config") as string) : {};
      createMutation.mutate({
        name: formData.get("name") as string,
        type: formData.get("type") as "deterministic" | "rubric" | "human",
        config,
        version: parseInt(formData.get("version") as string) || 1,
        promptVersion: (formData.get("promptVersion") as string) || null,
      });
    } catch {
      alert("Invalid JSON in config");
      return;
    }
    setIsCreateOpen(false);
    e.currentTarget.reset();
  };

  const handleUpdate = (graderId: string) => {
    try {
      const config = editConfig ? JSON.parse(editConfig) : {};
      updateMutation.mutate({
        graderId,
        input: {
          name: editName,
          type: editType,
          config,
          version: editVersion,
          promptVersion: editPromptVersion || null,
        },
      });
    } catch {
      alert("Invalid JSON in config");
      return;
    }
    setEditingId(null);
  };

  const handleDelete = (graderId: string) => {
    if (confirm("Are you sure you want to delete this grader?")) {
      deleteMutation.mutate(graderId);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Graders"
        headingLevel={headingLevel}
        description="Define how eval runs are scored — deterministic checks, rubrics, or human review."
        actions={
          <Button onClick={() => setIsCreateOpen(true)}>
            <Plus className="mr-2 h-4 w-4" /> New grader
          </Button>
        }
      />

      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search graders..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {isCreateOpen && (
            <form
              onSubmit={handleCreate}
              className="mb-4 p-4 border rounded-lg bg-muted/30 space-y-3"
            >
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium mb-1">Name</label>
                  <Input name="name" placeholder="Exact match grader" required autoFocus />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Type</label>
                  <Select
                    name="type"
                    defaultValue="deterministic"
                    onValueChange={(v) => setEditType(v as "deterministic" | "rubric" | "human")}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="deterministic">Deterministic</SelectItem>
                      <SelectItem value="rubric">Rubric</SelectItem>
                      <SelectItem value="human">Human</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium mb-1">Version</label>
                  <Input name="version" type="number" min="1" defaultValue="1" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Prompt version (optional)
                  </label>
                  <Input name="promptVersion" placeholder="v1.0.0" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Config (JSON)</label>
                <textarea
                  name="config"
                  placeholder='{"threshold": 0.8, "caseSensitive": false}'
                  className="w-full p-2 text-sm font-mono border rounded-md bg-background"
                  rows={3}
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={createMutation.isPending}>
                  {createMutation.isPending ? "Creating..." : "Create grader"}
                </Button>
              </div>
            </form>
          )}

          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <caption className="sr-only">Graders</caption>
              <thead>
                <tr className="border-b">
                  <th
                    scope="col"
                    className="h-10 px-3 text-left text-[0.8125rem] font-medium whitespace-nowrap text-muted-foreground first:pl-5 last:pr-5"
                  >
                    Name
                  </th>
                  <th
                    scope="col"
                    className="h-10 px-3 text-left text-[0.8125rem] font-medium whitespace-nowrap text-muted-foreground first:pl-5 last:pr-5"
                  >
                    Type
                  </th>
                  <th
                    scope="col"
                    className="h-10 px-3 text-left text-[0.8125rem] font-medium whitespace-nowrap text-muted-foreground first:pl-5 last:pr-5"
                  >
                    Version
                  </th>
                  <th
                    scope="col"
                    className="h-10 px-3 text-left text-[0.8125rem] font-medium whitespace-nowrap text-muted-foreground first:pl-5 last:pr-5"
                  >
                    Prompt version
                  </th>
                  <th
                    scope="col"
                    className="h-10 px-3 text-left text-[0.8125rem] font-medium whitespace-nowrap text-muted-foreground first:pl-5 last:pr-5"
                  >
                    Updated
                  </th>
                  <th
                    scope="col"
                    className="h-10 px-3 text-left text-[0.8125rem] font-medium whitespace-nowrap text-muted-foreground first:pl-5 last:pr-5"
                  ></th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={6} className="text-center py-8 text-muted-foreground">
                      Loading graders...
                    </td>
                  </tr>
                ) : filteredGraders.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-8">
                      <p className="font-medium">No graders yet</p>
                      <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
                        Create a grader to define how eval runs should be scored.
                      </p>
                    </td>
                  </tr>
                ) : (
                  filteredGraders.map((grader) => (
                    <GraderRow
                      key={grader.id}
                      grader={grader}
                      onEdit={() => {
                        setEditName(grader.name);
                        setEditType(grader.type);
                        setEditConfig(JSON.stringify(grader.config, null, 2));
                        setEditVersion(grader.version);
                        setEditPromptVersion(grader.promptVersion ?? "");
                        setEditingId(grader.id);
                      }}
                      onDelete={() => handleDelete(grader.id)}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>

          {editingId && (
            <div className="mt-4 p-4 border rounded-lg bg-muted/30 space-y-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium mb-1">Name</label>
                  <Input
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    required
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Type</label>
                  <Select
                    value={editType}
                    onValueChange={(v) => setEditType(v as "deterministic" | "rubric" | "human")}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="deterministic">Deterministic</SelectItem>
                      <SelectItem value="rubric">Rubric</SelectItem>
                      <SelectItem value="human">Human</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium mb-1">Version</label>
                  <Input
                    type="number"
                    min="1"
                    value={editVersion}
                    onChange={(e) => setEditVersion(parseInt(e.target.value) || 1)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Prompt version (optional)
                  </label>
                  <Input
                    value={editPromptVersion}
                    onChange={(e) => setEditPromptVersion(e.target.value)}
                    placeholder="v1.0.0"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Config (JSON)</label>
                <textarea
                  value={editConfig}
                  onChange={(e) => setEditConfig(e.target.value)}
                  className="w-full p-2 text-sm font-mono border rounded-md bg-background"
                  rows={3}
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setEditingId(null)}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={() => handleUpdate(editingId)}
                  disabled={updateMutation.isPending}
                >
                  {updateMutation.isPending ? "Saving..." : "Save changes"}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
